import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  X,
  FileText,
  ExternalLink,
  Lightbulb,
} from 'lucide-react';

// Gap types as defined in PRD
export type GapType = 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated';

// Severity levels
export type Severity = 'critical' | 'high' | 'medium' | 'low';

// Gap finding interface
export interface GapFinding {
  id: string;
  gap_type: GapType;
  severity: Severity;
  source_clause_id: string;
  source_clause_text: string;
  source_clause_reference: string; // e.g., "Article 7, Section 7.1, Clause 7.1.1"
  target_clause_id?: string;
  target_clause_text?: string;
  target_clause_reference?: string;
  description: string;
  recommendation: string;
  cfr_citation?: string;
  created_at: string;
}

export interface GapFindingsTableProps {
  findings: GapFinding[];
  sourceContractName: string;
  targetContractName: string;
  isLoading?: boolean;
  onExportCSV?: () => void;
  onFindingClick?: (finding: GapFinding) => void;
  testId?: string;
}

// Severity configuration using design system colors
const severityConfig: Record<Severity, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.FC<{ className?: string }>;
}> = {
  critical: {
    label: 'Critical',
    color: 'text-danger-700',
    bgColor: 'bg-danger-100',
    borderColor: 'border-danger-200',
    icon: AlertTriangle,
  },
  high: {
    label: 'High',
    color: 'text-warning-700',
    bgColor: 'bg-warning-100',
    borderColor: 'border-warning-200',
    icon: AlertCircle,
  },
  medium: {
    label: 'Medium',
    color: 'text-warning-600',
    bgColor: 'bg-warning-50',
    borderColor: 'border-warning-200',
    icon: Info,
  },
  low: {
    label: 'Low',
    color: 'text-primary-700',
    bgColor: 'bg-primary-100',
    borderColor: 'border-primary-200',
    icon: Info,
  },
};

// Gap type configuration
const gapTypeConfig: Record<GapType, { label: string; description: string }> = {
  missing_requirement: {
    label: 'Missing Requirement',
    description: 'Target contract lacks requirement present in source',
  },
  weaker_language: {
    label: 'Weaker Language',
    description: 'Target contract has less specific or weaker language',
  },
  ambiguous: {
    label: 'Ambiguous',
    description: 'Language is unclear or open to interpretation',
  },
  outdated: {
    label: 'Outdated',
    description: 'Requirement does not reflect current regulations',
  },
};

// Severity badge component
const SeverityBadge: React.FC<{ severity: Severity }> = ({ severity }) => {
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      data-testid={`severity-badge-${severity}`}
    >
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </span>
  );
};

// Gap type badge component
const GapTypeBadge: React.FC<{ gapType: GapType }> = ({ gapType }) => {
  const config = gapTypeConfig[gapType];

  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700"
      title={config.description}
      data-testid={`gap-type-badge-${gapType}`}
    >
      {config.label}
    </span>
  );
};

// Filter dropdown component with enhanced styling
const FilterDropdown: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  testId?: string;
}> = ({ label, options, value, onChange, testId }) => {
  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-neutral-600">{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-neutral-400 transition-colors cursor-pointer"
        data-testid={testId}
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Expandable row detail component with enhanced styling
const GapDetailPanel: React.FC<{
  finding: GapFinding;
  sourceContractName: string;
  targetContractName: string;
}> = ({ finding, sourceContractName, targetContractName }) => {
  return (
    <div className="bg-neutral-50 border-t border-neutral-200 p-6" data-testid={`gap-detail-${finding.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Source Clause */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm hover:shadow-card transition-shadow">
          <div className="flex items-center mb-3">
            <div className="p-1.5 bg-primary-100 rounded-lg mr-2">
              <FileText className="h-4 w-4 text-primary-600" />
            </div>
            <h4 className="text-sm font-semibold text-neutral-900">
              Source: {sourceContractName}
            </h4>
          </div>
          <p className="text-xs text-neutral-500 mb-2 font-medium" data-testid={`source-reference-${finding.id}`}>
            {finding.source_clause_reference}
          </p>
          <p className="text-sm text-neutral-700 leading-relaxed" data-testid={`source-text-${finding.id}`}>
            "{finding.source_clause_text}"
          </p>
        </div>

        {/* Target Clause (if exists) */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm hover:shadow-card transition-shadow">
          <div className="flex items-center mb-3">
            <div className="p-1.5 bg-secondary-100 rounded-lg mr-2">
              <FileText className="h-4 w-4 text-secondary-600" />
            </div>
            <h4 className="text-sm font-semibold text-neutral-900">
              Target: {targetContractName}
            </h4>
          </div>
          {finding.target_clause_text ? (
            <>
              <p className="text-xs text-neutral-500 mb-2 font-medium" data-testid={`target-reference-${finding.id}`}>
                {finding.target_clause_reference}
              </p>
              <p className="text-sm text-neutral-700 leading-relaxed" data-testid={`target-text-${finding.id}`}>
                "{finding.target_clause_text}"
              </p>
            </>
          ) : (
            <p className="text-sm text-neutral-400 italic" data-testid={`no-target-${finding.id}`}>
              No corresponding clause found in target contract
            </p>
          )}
        </div>
      </div>

      {/* CFR Citation */}
      {finding.cfr_citation && (
        <div className="mt-4 flex items-center gap-2 px-1">
          <ExternalLink className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-600">Regulatory Reference:</span>
          <span
            className="text-sm text-primary-600 font-medium"
            data-testid={`cfr-citation-${finding.id}`}
          >
            {finding.cfr_citation}
          </span>
        </div>
      )}

      {/* Recommendation */}
      <div className="mt-4 bg-primary-50 rounded-xl border border-primary-200 p-4">
        <div className="flex items-start">
          <div className="p-1.5 bg-primary-100 rounded-lg mr-3 flex-shrink-0">
            <Lightbulb className="h-4 w-4 text-primary-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-primary-900 mb-1">Recommendation</h4>
            <p className="text-sm text-primary-800 leading-relaxed" data-testid={`recommendation-${finding.id}`}>
              {finding.recommendation}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Convert findings to CSV format
const exportToCSV = (
  findings: GapFinding[],
  sourceContractName: string,
  targetContractName: string
): void => {
  const headers = [
    'ID',
    'Gap Type',
    'Severity',
    'Description',
    'Source Contract',
    'Source Reference',
    'Source Clause',
    'Target Contract',
    'Target Reference',
    'Target Clause',
    'Recommendation',
    'CFR Citation',
    'Created At',
  ];

  const rows = findings.map((f) => [
    f.id,
    gapTypeConfig[f.gap_type].label,
    severityConfig[f.severity].label,
    `"${f.description.replace(/"/g, '""')}"`,
    sourceContractName,
    f.source_clause_reference,
    `"${f.source_clause_text.replace(/"/g, '""')}"`,
    targetContractName,
    f.target_clause_reference || '',
    f.target_clause_text ? `"${f.target_clause_text.replace(/"/g, '""')}"` : '',
    `"${f.recommendation.replace(/"/g, '""')}"`,
    f.cfr_citation || '',
    f.created_at,
  ]);

  const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `gap-analysis-${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Main component
export const GapFindingsTable: React.FC<GapFindingsTableProps> = ({
  findings,
  sourceContractName,
  targetContractName,
  isLoading = false,
  onExportCSV,
  onFindingClick,
  testId = 'gap-findings-table',
}) => {
  // Filter state
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [gapTypeFilter, setGapTypeFilter] = useState<string>('');

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Apply filters
  const filteredFindings = useMemo(() => {
    return findings.filter((f) => {
      if (severityFilter && f.severity !== severityFilter) return false;
      if (gapTypeFilter && f.gap_type !== gapTypeFilter) return false;
      return true;
    });
  }, [findings, severityFilter, gapTypeFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const bySeverity = {
      critical: findings.filter((f) => f.severity === 'critical').length,
      high: findings.filter((f) => f.severity === 'high').length,
      medium: findings.filter((f) => f.severity === 'medium').length,
      low: findings.filter((f) => f.severity === 'low').length,
    };
    return { bySeverity, total: findings.length };
  }, [findings]);

  const toggleRowExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSeverityFilter('');
    setGapTypeFilter('');
  };

  const hasFilters = severityFilter || gapTypeFilter;

  const handleExportCSV = () => {
    if (onExportCSV) {
      onExportCSV();
    } else {
      exportToCSV(filteredFindings, sourceContractName, targetContractName);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-card border border-neutral-100 overflow-hidden" data-testid={`${testId}-loading`}>
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="h-20 bg-neutral-100 p-4 flex flex-col justify-center gap-2">
            <div className="h-5 bg-neutral-200 rounded w-48" />
            <div className="h-3 bg-neutral-200 rounded w-32" />
          </div>
          {/* Filters skeleton */}
          <div className="h-14 bg-white border-b border-neutral-100 p-4 flex items-center gap-4">
            <div className="h-8 bg-neutral-200 rounded w-24" />
            <div className="h-8 bg-neutral-200 rounded w-32" />
            <div className="h-8 bg-primary-200 rounded w-28 ml-auto" />
          </div>
          {/* Rows skeleton */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`h-16 border-t border-neutral-100 flex items-center px-6 gap-6 ${i % 2 === 0 ? 'bg-neutral-50/50' : 'bg-white'}`}>
              <div className="h-4 w-4 bg-neutral-200 rounded" />
              <div className="h-5 bg-neutral-200 rounded-full w-20" />
              <div className="h-5 bg-neutral-200 rounded-full w-28" />
              <div className="h-4 bg-neutral-200 rounded w-64 flex-1" />
              <div className="h-3 bg-neutral-200 rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-card border border-neutral-100 overflow-hidden hover:shadow-card-hover transition-shadow" data-testid={testId}>
      {/* Header with stats and export */}
      <div className="border-b border-neutral-200 p-4 bg-neutral-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900" data-testid="gap-findings-title">
              Gap Analysis Results
            </h3>
            <p className="text-sm text-neutral-500" data-testid="gap-findings-subtitle">
              {sourceContractName} vs {targetContractName}
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm flex-wrap" data-testid="gap-stats-summary">
            <span className="font-semibold text-neutral-700">{stats.total} gaps:</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-danger-100 text-danger-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-danger-500"></span>
              {stats.bySeverity.critical} critical
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-warning-100 text-warning-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-warning-500"></span>
              {stats.bySeverity.high} high
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-warning-50 text-warning-600 font-medium">
              <span className="w-2 h-2 rounded-full bg-warning-400"></span>
              {stats.bySeverity.medium} medium
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              {stats.bySeverity.low} low
            </span>
          </div>
        </div>
      </div>

      {/* Filters toolbar */}
      <div className="border-b border-neutral-100 p-4 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-semibold">Filters:</span>
            </div>
            <FilterDropdown
              label="Severity"
              options={[
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              value={severityFilter}
              onChange={setSeverityFilter}
              testId="severity-filter"
            />
            <FilterDropdown
              label="Gap Type"
              options={[
                { value: 'missing_requirement', label: 'Missing Requirement' },
                { value: 'weaker_language', label: 'Weaker Language' },
                { value: 'ambiguous', label: 'Ambiguous' },
                { value: 'outdated', label: 'Outdated' },
              ]}
              value={gapTypeFilter}
              onChange={setGapTypeFilter}
              testId="gap-type-filter"
            />
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 px-2 py-1 rounded-md transition-colors"
                data-testid="clear-filters"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium shadow-sm"
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
        {hasFilters && (
          <div className="mt-3 text-sm text-neutral-500 bg-neutral-50 px-3 py-2 rounded-lg" data-testid="filter-results-count">
            Showing <span className="font-medium text-neutral-700">{filteredFindings.length}</span> of {findings.length} gaps
          </div>
        )}
      </div>

      {/* Table */}
      {filteredFindings.length === 0 ? (
        <div className="p-12 text-center" data-testid="no-findings">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-1">No gaps found</h3>
          <p className="text-sm text-neutral-500 max-w-sm mx-auto">
            {hasFilters
              ? 'No gaps match your current filters. Try adjusting the filter criteria.'
              : 'No compliance gaps were identified in this analysis.'}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline"
              data-testid="clear-filters-empty"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100">
          <table className="min-w-full divide-y divide-neutral-200" data-testid="findings-table">
            <thead className="bg-neutral-50">
              <tr>
                <th className="w-12 px-4 py-3" />
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Gap Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Source Location
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-100">
              {filteredFindings.map((finding, index) => {
                const isExpanded = expandedRows.has(finding.id);

                return (
                  <React.Fragment key={finding.id}>
                    <tr
                      className={`cursor-pointer transition-colors ${
                        isExpanded
                          ? 'bg-primary-50/50'
                          : index % 2 === 1
                            ? 'bg-neutral-50/50 hover:bg-primary-50/50'
                            : 'bg-white hover:bg-primary-50/50'
                      }`}
                      onClick={() => {
                        toggleRowExpanded(finding.id);
                        onFindingClick?.(finding);
                      }}
                      data-testid={`finding-row-${finding.id}`}
                    >
                      <td className="px-4 py-4">
                        <button
                          className={`p-1 rounded-md transition-colors ${
                            isExpanded
                              ? 'text-primary-600 bg-primary-100'
                              : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
                          }`}
                          data-testid={`expand-row-${finding.id}`}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <SeverityBadge severity={finding.severity} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <GapTypeBadge gapType={finding.gap_type} />
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-neutral-900 line-clamp-2 leading-relaxed"
                          data-testid={`finding-description-${finding.id}`}
                        >
                          {finding.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="text-sm text-neutral-500 font-mono bg-neutral-100 px-2 py-1 rounded"
                          data-testid={`finding-location-${finding.id}`}
                        >
                          {finding.source_clause_reference}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={5} className="p-0">
                          <GapDetailPanel
                            finding={finding}
                            sourceContractName={sourceContractName}
                            targetContractName={targetContractName}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
